import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { formatBucketLabel } from './chartDate';
import { ChartStyleToggle } from './ChartStyleToggle';
import type { ChartStyle } from './chartPrefs';
import { formatMetricValue, type TrendChartData } from './trendSeries';

const PRIMARY_COLOR = 'hsl(var(--chart-1))';

const GROUPING_LABEL = { daily: 'daily', weekly: 'weekly', monthly: 'monthly' } as const;

// The big interactive trend chart — a single series (currency is global) for the
// selected hero metric, in the metric's chart style (bar/line/area). Header
// carries the style toggle + a collapse control; grouping is set globally.
export function ReportingTrendChart({
  data,
  style,
  onStyleChange,
  minimised,
  onToggleMinimise,
}: {
  data: TrendChartData;
  style: ChartStyle;
  onStyleChange: (style: ChartStyle) => void;
  minimised: boolean;
  onToggleMinimise: () => void;
}) {
  const config: ChartConfig = { value: { label: data.metric.label, color: PRIMARY_COLOR } };

  const header = (
    <div className="flex items-center justify-between gap-3">
      {/* Full-weight + full-contrast, matching every other panel heading on the
          canvas — see PanelTitle for why a panel's title is not secondary text. */}
      <h3 className="text-sm font-semibold lowercase text-foreground">
        {data.metric.label} over time
        <span className="ml-2 text-xs font-normal text-muted-foreground">
          {GROUPING_LABEL[data.grouping]}
        </span>
      </h3>
      <div className="flex items-center gap-2">
        <ChartStyleToggle value={style} onChange={onStyleChange} />
        <button
          type="button"
          onClick={onToggleMinimise}
          aria-expanded={!minimised}
          title={minimised ? 'Expand chart' : 'Collapse chart'}
          className="rounded border border-slate-200 bg-white p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
        >
          {minimised ? (
            <ChevronsUpDown className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronsDownUp className="h-4 w-4" aria-hidden />
          )}
        </button>
      </div>
    </div>
  );

  const name = data.metric.label;
  // Shared as single ELEMENTS (never a Fragment) so they stay DIRECT children of
  // the chart — Recharts discovers graphical children via React.Children.forEach,
  // which does not descend into fragments (a fragment-wrapped child renders blank).
  const grid = <CartesianGrid vertical={false} strokeDasharray="3 3" />;
  const xAxis = (
    <XAxis
      dataKey="bucket"
      tickLine={false}
      axisLine={false}
      tickMargin={8}
      minTickGap={24}
      tickFormatter={(b) => formatBucketLabel(String(b), data.grouping)}
    />
  );
  const yAxis = <YAxis hide />;
  const tooltip = (
    <ChartTooltip
      cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }}
      content={
        <ChartTooltipContent
          labelFormatter={(v) => formatBucketLabel(String(v), data.grouping)}
          formatter={(value) => (
            <span className="font-mono font-medium tabular-nums text-foreground">
              {formatMetricValue(data.format, Number(value), data.currency)}
            </span>
          )}
        />
      }
    />
  );
  const common = { data: data.rows, margin: { left: 4, right: 4, top: 4, bottom: 0 }, accessibilityLayer: true };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className={minimised ? '' : 'mb-3'}>{header}</div>
      {!minimised &&
        (data.isEmpty ? (
          <div className="flex h-64 items-center justify-center text-sm text-slate-400">
            no {data.metric.label} data to plot for this range.
          </div>
        ) : (
          <ChartContainer config={config} className="aspect-auto h-64 w-full">
            {style === 'line' ? (
              <LineChart {...common}>
                {grid}
                {xAxis}
                {yAxis}
                {tooltip}
                <Line
                  type="monotone"
                  dataKey="value"
                  name={name}
                  stroke="var(--color-value)"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              </LineChart>
            ) : style === 'area' ? (
              <AreaChart {...common}>
                {grid}
                {xAxis}
                {yAxis}
                {tooltip}
                <Area
                  type="monotone"
                  dataKey="value"
                  name={name}
                  stroke="var(--color-value)"
                  fill="var(--color-value)"
                  fillOpacity={0.18}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              </AreaChart>
            ) : (
              <BarChart {...common}>
                {grid}
                {xAxis}
                {yAxis}
                {tooltip}
                <Bar
                  dataKey="value"
                  name={name}
                  fill="var(--color-value)"
                  radius={[2, 2, 0, 0]}
                  maxBarSize={48}
                  isAnimationActive={false}
                />
              </BarChart>
            )}
          </ChartContainer>
        ))}
    </div>
  );
}
